import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { Container, Row, Col, Spinner, Alert } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { Helmet } from "react-helmet";
import Header from "../components/header.component";
import { Footer } from "../components/footer.component";
import { AccountAccess } from "../components/account-access.component";
import { RootState, AppDispatch } from "../store";
import { fetchNewsBySlug } from "../features/news/news.slice";
import "../scss/news-styling-component/news-detail.style.scss";
import { shareOnSocialMedia, socialIcons } from "../constant/social-icon.constant";
import { formatDate } from "../helper/formatdate.helper";
import Loader from '../../components/Loader'
import { Calendar, GreyTimer } from "../components/images/svg/icons";
const NewsDetailPage: React.FC = () => {
    const { slug } = useParams<{ slug: string }>();
    const navigate = useNavigate();
    const dispatch = useDispatch<AppDispatch>();
    const { t } = useTranslation();

    type SocialPlatform = 'facebook' | 'twitter' | 'linkedin';

    function handleShareClick(platform: SocialPlatform): void {
        const url = encodeURIComponent(location.href);
        const shareUrls = {
            facebook: `https://www.facebook.com/sharer/sharer.php?u=${url}`,
            twitter: `https://twitter.com/intent/tweet?url=${url}`,
            linkedin: `https://www.linkedin.com/shareArticle?mini=true&url=${url}`
        };

        const shareUrl = shareUrls[platform];
        if (shareUrl) window.open(shareUrl, '_blank');
    }

    const { selectedNews, loading, error } = useSelector((state: RootState) => state.news);

    useEffect(() => {
        dispatch(fetchNewsBySlug(slug))
    }, [slug, dispatch, navigate]);

    if (loading) {
        return (
            <>
                <Header />
                <Container className="d-flex justify-content-center align-items-center" style={{ minHeight: "60vh" }}>
                    <Loader />
                </Container>
                <div className="bg-footer">
                    <AccountAccess />
                    <Footer />
                </div>
            </>
        );
    }
    return (
        <>
            <Helmet>
                <title>{selectedNews?.metaTitle || selectedNews?.heading}</title>
                <meta name="description" content={selectedNews?.metaDescription || selectedNews?.shortContent} />
                <meta name="keywords" content={selectedNews?.metaKeywords || ""} />
                <meta property="og:title" content={selectedNews?.heading} />
                <meta property="og:description" content={selectedNews?.shortContent} />
                <meta property="og:type" content="article" />
                <meta property="og:url" content={window.location.href} />
                {selectedNews?.thumbnailUrl && (
                    <meta property="og:image" content={selectedNews?.thumbnailUrl} />
                )}
                <meta name="article:published_time" content={selectedNews?.createdDate} />
                <meta name="article:author" content={selectedNews?.nametoShow || "Invozio"} />
                <link rel="canonical" href={window.location.href} />
            </Helmet>
            <Header />
            {/* Social Media Banner */}
            <div className="social-media-banner">
                <Container>
                    <Row className="align-items-center justify-content-between">
                        <Col xs={12} md={8} className="px-0">
                            <span className="banner-text">
                                Spread the word about Invozio on social media and help our community grow!
                            </span>
                        </Col>
                        <Col xs={12} md={4} className="text-end px-0">
                            <div className="social-icons">
                                {(shareOnSocialMedia.map((icon, index) => (
                                    <Link to={`${location.pathname}${location.search || ''}`}
                                        key={index} className="social-icon" onClick={() => handleShareClick(icon.platform as SocialPlatform)}>
                                        <img src={icon.icon} alt="" />
                                    </Link>
                                )))}
                            </div>
                        </Col>
                    </Row>
                </Container>
            </div>

            <Container>
                <div className="news-detail-header text-center mt-5">
                    <div className="d-flex justify-content-center align-items-center gap-3 flex-wrap ">
                        <div className="d-flex align-items-center">
                            <img src={GreyTimer} alt={t("ALT_TIME")} className="news-card-icon mr-1" />
                            <span className="color-252525">{selectedNews?.readTimeInMinutes} Minutes Read</span>
                        </div>
                        <div className="d-flex align-items-center">
                            <img src={Calendar} alt={t("ALT_DATE")} className="news-card-icon mr-1" />
                            <span className="color-252525">{formatDate(selectedNews?.createdDate)}</span>
                        </div>
                    </div>
                </div>

                <Row>
                    <Col xs={12} className="px-0">
                        <div
                            className="news-full-content my-5"
                            dangerouslySetInnerHTML={{ __html: selectedNews?.fullContent }}
                        >
                        </div>
                    </Col>
                </Row>
            </Container>
            <div className="bg-footer">
                <AccountAccess />
                <Footer />
            </div>
        </>
    );
};

export default NewsDetailPage;
